import React, { useEffect } from "react";
import { X } from "lucide-react";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "full";
  footer?: React.ReactNode;
  bodyClassName?: string;
}

const sizeClasses: Record<NonNullable<ModalProps["size"]>, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  full: "max-w-full mx-4 md:mx-10",
};

const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  children,
  size = "md",
  footer,
  bodyClassName,
}) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center overflow-y-auto">
      <div
        className={`bg-white rounded-2xl shadow-lg w-full relative flex flex-col max-h-[85vh] overflow-hidden dark:bg-gray-800 ${sizeClasses[size]}`}
      >
        <button
          className="absolute top-5 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          onClick={onClose}
        >
          <X size={18} />
        </button>
        {title && (
          <h2 className="text-lg font-semibold border-b border-gray-200 p-4 shrink-0 dark:border-gray-700 dark:text-gray-100">
            {title}
          </h2>
        )}
        <div className={bodyClassName || "p-4 overflow-y-auto flex-1"}>{children}</div>
        {footer && (
          <div className="border-t border-gray-200 p-4 flex justify-end gap-2 shrink-0 dark:border-gray-700">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
